import type { ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";

import { featureFlags } from "@common/feature-flags";
import { PageContent } from "@/components/page";
import { ServiceCard } from "@/components/service-card";
import { Button } from "@/components/ui/button";
import {
  Modal,
  ModalContent,
  ModalTitle,
  useMuiModal,
} from "@/components/modal";
import { formatCurrency } from "@/lib/utils";
import { recordTrackedEvent } from "@/lib/kiosk-session-tracking";
import { useShop } from "@/hooks/use-shop";
import { useUser } from "@/hooks/use-user";
import type { PaymentType } from "@/features/payment/utils";
import { MyCouponsList } from "@/features/coupons/my-coupons-list";
import { useKioskNthPurchaseCampaignHold } from "@/features/nth-purchase-campaign/nth-purchase-campaign-banner";
import { useAudioSlot } from "@/features/audio/kiosk-audio";
import { Strikethrough } from "@/routes/machine-selector-screen.$machine-type";

export type PaymentMethod = "cash" | "card" | "balance" | "free";

const PURCHASE_TYPES = {
  cash: "KIOSK_CASH",
  card: "KIOSK_BANK_CARD",
  balance: "BALANCE",
  free: "FULL_DISCOUNT",
} as const;

/**
 * The payment method choice of a purchase: price, coupons, the
 * nth-purchase-campaign discount and one card per payment method (or a start
 * button when it's free). Shared by the payment selector screen and the
 * payment flow; `onChoose` gets the method once the user confirmed it.
 */
export function PaymentMethodChoice({
  payment,
  machineId,
  activeCouponCode,
  machineSummary,
  pending,
  onReloadPayment,
  onChoose,
  onEnterCoupon,
}: {
  payment: PaymentType;
  machineId: number;
  activeCouponCode: string | null;
  // shown left of the price
  machineSummary?: ReactNode;
  // a chosen method is being started
  pending: boolean;
  // re-creates the payment, with or without the coupon
  onReloadPayment: () => Promise<unknown>;
  onChoose: (method: PaymentMethod) => void;
  // "Kupon megadása"; defaults to the coupon screen
  onEnterCoupon?: () => void;
}) {
  const { t } = useTranslation();
  const { showModal } = useMuiModal();
  const { data: shop } = useShop();
  const { data: user } = useUser();
  const navigate = useNavigate();
  const { hold } = useKioskNthPurchaseCampaignHold(shop.shopId);
  const { campaignApplied, couponAllowed } = nthPurchaseCampaignState(
    hold,
    payment,
  );
  const choose = async (method: PaymentMethod) => {
    if (
      method === "cash" &&
      !featureFlags.kiosk.newCashUi &&
      !(await showModal<boolean>(<CashModal />))
    ) {
      return;
    }
    void recordTrackedEvent({
      eventType: "payment_method_selected",
      machineId: payment.machineId,
      dryerBundleId: payment.dryerBundleId,
      kioskPaymentNonceKey: payment.kioskPaymentNonceKey,
      payload: { purchaseType: PURCHASE_TYPES[method] },
    });
    onChoose(method);
  };

  return (
    <>
      <PageContent>
        <div className="flex justify-center items-center mb-8">
          {machineSummary && (
            <>
              {machineSummary}
              <div className="border-2 border-bub-darker-blue h-[144px]"></div>
            </>
          )}
          <div className="text-center min-w-64 text-2xl">
            <p className="mb-8">
              <Trans>Fizetendő</Trans>
            </p>
            <div className="relative flex flex-col items-center">
              {!!payment.originalPrice &&
                payment.originalPrice !== 0 &&
                payment.originalPrice !== payment.price && (
                  <Strikethrough className="absolute -top-6 mx-auto">
                    {formatCurrency(payment.originalPrice, shop.currencyCode)}
                  </Strikethrough>
                )}
              <h2 className="font-fancy text-5xl">
                {formatCurrency(payment.price, shop.currencyCode)}
              </h2>
              {/* reservations and dryer extra time never get it */}
              {hold && campaignApplied && (
                <p className="mt-3 rounded-full bg-bub-orange/15 px-4 py-1 text-[20px] font-bold text-bub-orange">
                  <Trans>
                    Kampány kedvezmény: −{{ percentage: hold.percentage }}%
                  </Trans>
                </p>
              )}
            </div>
          </div>
        </div>
        {hold && couponAllowed && (
          <NthPurchaseCampaignCouponChoice
            percentage={hold.percentage}
            couponReplacesCampaign={!!activeCouponCode && !campaignApplied}
            onUseCampaign={() => {
              sessionStorage.removeItem("coupon");
              void onReloadPayment();
            }}
          />
        )}
        {couponAllowed && (
          <MyCouponsList
            machineId={machineId}
            dryerBundleId={payment.dryerBundleId}
            currency={shop.currencyCode}
            activeCode={activeCouponCode}
            onApplied={onReloadPayment}
          />
        )}
        {couponAllowed && (
          <div className="grid grid-cols-3 gap-12 items-center justify-center mb-8">
            <p className="font-fancy text-4xl text-center">
              <Trans>Van kuponod?</Trans>
            </p>
            <Button
              variant="orange"
              className="text-2xl font-bold px-2 max-w-max mx-auto"
              onClick={() => {
                void recordTrackedEvent({
                  eventType: "coupon_entry_opened",
                  machineId,
                  dryerBundleId: payment.dryerBundleId,
                });
                if (onEnterCoupon) {
                  onEnterCoupon();
                  return;
                }
                navigate({
                  to: "/coupon-screen/{-$machineId}/{-$dryerBundleId}",
                  params: { machineId, dryerBundleId: payment.dryerBundleId },
                });
              }}
            >
              <Trans>Kupon megadása</Trans>
            </Button>
            <p className="font-fancy text-4xl text-center">
              <Trans>Használd fel!</Trans>
            </p>
          </div>
        )}
      </PageContent>
      {payment.price === 0 ? (
        <div className="flex justify-center mb-16 px-6">
          <Button
            className="font-bold w-xl py-7 text-4xl px-0"
            disabled={pending}
            onClick={() => choose("free")}
          >
            <Trans>Gép indítása</Trans>
          </Button>
        </div>
      ) : (
        <div className="flex gap-10 grid-cols-3 items-center justify-center mb-16 px-6">
          {shop.kioskSettings.includes("cash") && (
            <ServiceCard
              img="/select-cash.svg"
              title={t("Készpénz")}
              onClick={() => choose("cash")}
            />
          )}
          {shop.kioskSettings.includes("terminal") && (
            <ServiceCard
              img="/select-card.svg"
              disabledInOfflineMode
              title={t("Kártya")}
              onClick={() => choose("card")}
            />
          )}
          {shop.kioskSettings.includes("balance") && user && (
            <ServiceCard
              img="/select-balance.svg"
              title={t("Egyenleg")}
              onClick={() => choose("balance")}
            />
          )}
        </div>
      )}
      {shop.canGenerateInvoices && user && (
        <p className="flex gap-x-1 items-center text-2xl mx-auto justify-center">
          <img src="/arrow-attention.svg" className="size-8" />
          <Trans>
            Számládat egyszerűen letöltheted a my.bubbles.hu oldalról.
          </Trans>
        </p>
      )}
    </>
  );
}

export function PaymentMethodHelpText() {
  const { data: user } = useUser();
  return user ? (
    <Trans>
      Válassz fizetési módot! Bankkártyával, vagy - amennyiben elég fedezet van
      rajta - az egyenlegedről fizethetsz.
    </Trans>
  ) : (
    <Trans>Válassz fizetési módot! Bankkártyás fizetés.</Trans>
  );
}

export function CashModal() {
  useAudioSlot("modal.cash-info");
  const { data: shop } = useShop();
  const { closeModal } = useMuiModal();
  const user = useUser();

  return (
    <Modal>
      <ModalTitle>
        <Trans>Figyelmeztetés!</Trans>
      </ModalTitle>
      <ModalContent>
        <p>
          {shop.currency === "EUR" ? (
            <Trans>
              Készpénzes fizetéshez 0.5, 1.00 és 2.00 € érmét, vagy 5, 10, 20 és
              50 € bankjegyet tudunk elfogadni.
            </Trans>
          ) : (
            <Trans>
              Készpénzes fizetéshez 100, 200 Ft-os érmét, vagy 500, 1000 és 2000
              Ft-os bankjegyet tudunk elfogadni.
            </Trans>
          )}
        </p>
        {user.data && !shop.returnsChangeToLoggedInUser && (
          <Trans>
            Regisztrált felhasználóknak az esetleges visszajárót a Bubbles
            egyenlegre helyezzük fel, mely a későbbiekben felhasználható.
          </Trans>
        )}
        {!user.data && !shop.canReturnNotes && (
          <Trans>
            A visszajárót a gép csak érmékben tudja visszaadni, maximum 900
            Ft-ot, ezért kérjük, az utolsónak behelyezett címlet ne legyen 1000
            Ft-nál nagyobb!
          </Trans>
        )}
        <p>
          <Trans>
            Nagyobb összegű fizetés esetén bankkártya használatát javasoljuk.
          </Trans>
        </p>
        <div className="flex gap-6 mt-8">
          <Button
            onClick={() => closeModal(false)}
            variant="outline"
            className="px-6"
          >
            <Trans>Mégsem</Trans>
          </Button>
          <Button
            onClick={() => closeModal(true)}
            size="icon"
            className="px-2"
          >
            <Trans>Értettem, tovább</Trans>
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}

/**
 * A coupon and the nth-purchase-campaign discount never combine. Under a
 * partial campaign discount the user may still pick a coupon instead (it may be
 * worth more); a 100% one leaves nothing to choose. Older cash-agents do not say
 * which one won and always drop the coupon, so they get no choice either.
 */
function nthPurchaseCampaignState(
  hold: { percentage: number } | null | undefined,
  payment: PaymentType,
) {
  if (!hold) {
    return { campaignApplied: false, couponAllowed: true };
  }
  if (payment.nthPurchaseCampaignDiscount === undefined) {
    return {
      campaignApplied: payment.originalPrice !== payment.price,
      couponAllowed: false,
    };
  }
  return {
    campaignApplied: payment.nthPurchaseCampaignDiscount !== null,
    couponAllowed: hold.percentage < 100,
  };
}

function NthPurchaseCampaignCouponChoice({
  percentage,
  couponReplacesCampaign,
  onUseCampaign,
}: {
  percentage: number;
  couponReplacesCampaign: boolean;
  onUseCampaign: () => void;
}) {
  if (!couponReplacesCampaign) {
    return (
      <p className="mb-8 text-center text-2xl text-gray-500">
        <Trans>
          Ha a kuponod többet ér, használhatod a −{{ percentage }}% kampány
          kedvezmény helyett. A kettő nem vonható össze.
        </Trans>
      </p>
    );
  }
  return (
    <div className="mb-8 flex flex-col items-center gap-4 rounded-2xl bg-bub-orange/15 p-6">
      <p className="text-center text-2xl font-bold text-bub-orange">
        <Trans>
          A kuponod a −{{ percentage }}% kampány kedvezmény helyett érvényes.
        </Trans>
      </p>
      <Button
        variant="orange"
        className="px-6 text-2xl font-bold"
        onClick={onUseCampaign}
      >
        <Trans>Inkább a kampány kedvezményt kérem</Trans>
      </Button>
    </div>
  );
}
